"use client";

import { useState, useEffect, useCallback } from "react";
import Image from "next/image";
import { AnimatePresence, motion } from "framer-motion";
import { FaWhatsapp } from "react-icons/fa6";
import Turnstile from "@/components/shared/Turnstile";

type MembershipType = "private" | "corporate" | null;

interface Labels {
  eyebrow: string;
  title: string;
  subtitle: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  message: string;
  submit: string;
  membershipPrivate: string;
  membershipCorporate: string;
  membershipLabel: string;
  quoteText: string;
  quoteAuthor: string;
  /** Textes optionnels (repli sur le texte d'origine selon la langue) */
  successTitle?: string;
  successText?: string;
  errorText?: string;
  sending?: string;
  orDirectly?: string;
}

const DEFAULT_SLIDES = [
  "/hero/slide-1.jpg",
  "/hero/slide-2.jpg",
  "/hero/slide-3.jpg",
];

export default function ContactForm({
  labels,
  locale = "fr",
  slides,
}: {
  labels: Labels;
  locale?: "fr" | "en";
  /** Images du carrousel (repli : images d'origine) */
  slides?: string[];
}) {
  const SLIDES = slides && slides.length > 0 ? slides : DEFAULT_SLIDES;
  const slideCount = SLIDES.length;
  const [membership, setMembership] = useState<MembershipType>(null);
  const [currentSlide, setCurrentSlide] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slideCount);
    }, 6000);
    return () => clearInterval(timer);
  }, [slideCount]);
  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    message: "",
  });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState("");

  const onToken = useCallback((token: string) => setTurnstileToken(token), []);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    setError(false);
    try {
      const res = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, membership, locale, turnstileToken }),
      });
      if (!res.ok) throw new Error("request_failed");
      setSubmitted(true);
    } catch {
      setError(true);
    } finally {
      setSubmitting(false);
    }
  };

  const inputClass =
    "w-full bg-transparent border-0 border-b border-white/20 pb-2 pt-1 text-sm text-white placeholder-white/30 focus:outline-none focus:border-white/60 transition-colors duration-300 font-body";

  const labelClass = "text-[10px] tracking-[0.18em] uppercase text-white/40 font-body";

  return (
    <section className="min-h-screen flex flex-col lg:flex-row">
      {/* ── LEFT — Form ───────────────────────────────────────────── */}
      <div className="flex-1 bg-[#0b0b0d] flex items-center justify-center px-8 sm:px-14 lg:px-20 py-24 lg:py-32">
        <div className="w-full max-w-[480px]">
          {/* Eyebrow */}
          <p className="text-[10px] tracking-[0.3em] uppercase text-white/35 font-body mb-6">
            {labels.eyebrow}
          </p>

          {/* Title */}
          <h1 className="font-heading text-4xl sm:text-5xl text-white font-light italic leading-tight mb-4">
            {labels.title}
          </h1>

          {/* Subtitle */}
          <p className="text-sm text-white/45 font-body leading-relaxed mb-12 max-w-sm">
            {labels.subtitle}
          </p>

          {submitted ? (
            /* ── Success state ── */
            <div className="flex flex-col items-start gap-4 py-8">
              <div className="w-10 h-px bg-white/40" />
              <p className="font-heading text-2xl text-white font-light italic">
                {labels.successTitle ?? (locale === "fr" ? "Message reçu." : "Message received.")}
              </p>
              <p className="text-sm text-white/45 font-body leading-relaxed">
                {labels.successText ??
                  (locale === "fr"
                    ? "Nous vous répondrons dans les meilleurs délais, avec le soin et la discrétion qui vous sont dus."
                    : "We will reply as soon as possible, with the care and discretion you deserve.")}
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="flex flex-col gap-9">
              {/* Membership type selector */}
              <div className="flex flex-col gap-3">
                <span className={labelClass}>{labels.membershipLabel}</span>
                <div className="grid grid-cols-2 gap-3">
                  {(
                    [
                      { key: "private" as const, label: labels.membershipPrivate, icon: "private" },
                      { key: "corporate" as const, label: labels.membershipCorporate, icon: "corporate" },
                    ] as const
                  ).map(({ key, label, icon }) => (
                    <button
                      key={key}
                      type="button"
                      onClick={() =>
                        setMembership((prev) => (prev === key ? null : key))
                      }
                      className={`flex items-center gap-2.5 border px-4 py-3 rounded-sm text-xs tracking-[0.12em] uppercase font-body transition-all duration-300 ${
                        membership === key
                          ? "border-white/60 text-white bg-white/5"
                          : "border-white/15 text-white/40 hover:border-white/35 hover:text-white/70"
                      }`}
                    >
                      {/* Icon */}
                      {icon === "private" ? (
                        <svg
                          width="14"
                          height="14"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          className="shrink-0 opacity-70"
                        >
                          <circle cx="12" cy="8" r="4" />
                          <path d="M4 20c0-4 3.6-7 8-7s8 3 8 7" />
                        </svg>
                      ) : (
                        <svg
                          width="14"
                          height="14"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          className="shrink-0 opacity-70"
                        >
                          <rect x="2" y="7" width="20" height="15" rx="1" />
                          <path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2" />
                          <line x1="12" y1="12" x2="12" y2="17" />
                          <line x1="9.5" y1="14.5" x2="14.5" y2="14.5" />
                        </svg>
                      )}
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Row 1 — Prénom / Nom */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-9">
                <div className="flex flex-col gap-2">
                  <label className={labelClass}>{labels.firstName}</label>
                  <input
                    name="firstName"
                    type="text"
                    required
                    value={form.firstName}
                    onChange={handleChange}
                    className={inputClass}
                  />
                </div>
                <div className="flex flex-col gap-2">
                  <label className={labelClass}>{labels.lastName}</label>
                  <input
                    name="lastName"
                    type="text"
                    required
                    value={form.lastName}
                    onChange={handleChange}
                    className={inputClass}
                  />
                </div>
              </div>

              {/* Row 2 — Email / Téléphone */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-9">
                <div className="flex flex-col gap-2">
                  <label className={labelClass}>{labels.email}</label>
                  <input
                    name="email"
                    type="email"
                    required
                    value={form.email}
                    onChange={handleChange}
                    className={inputClass}
                  />
                </div>
                <div className="flex flex-col gap-2">
                  <label className={labelClass}>{labels.phone}</label>
                  <input
                    name="phone"
                    type="tel"
                    value={form.phone}
                    onChange={handleChange}
                    className={inputClass}
                  />
                </div>
              </div>

              {/* Row 3 — Message */}
              <div className="flex flex-col gap-2">
                <label className={labelClass}>{labels.message}</label>
                <textarea
                  name="message"
                  rows={3}
                  value={form.message}
                  onChange={handleChange}
                  className={`${inputClass} resize-none`}
                />
              </div>

              {/* Turnstile (rendu uniquement si la clé publique est définie) */}
              <Turnstile onToken={onToken} />

              {error && (
                <p className="text-xs text-red-300/80 font-body">
                  {labels.errorText ??
                    (locale === "fr"
                      ? "Une erreur est survenue. Merci de réessayer."
                      : "Something went wrong. Please try again.")}
                </p>
              )}

              {/* Submit */}
              <div className="flex items-center justify-between pt-2">
                <button
                  type="submit"
                  disabled={submitting}
                  className="rounded-full bg-white text-black text-[10px] tracking-[0.22em] uppercase font-body font-medium px-10 py-3.5 hover:bg-white/90 transition-colors duration-200 cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {submitting
                    ? (labels.sending ??
                      (locale === "fr" ? "Envoi…" : "Sending…"))
                    : labels.submit}
                </button>

                {/* Decorative arrow */}
                <span className="text-white/20 text-lg select-none">↗</span>
              </div>
            </form>
          )}

          {/* ── Coordonnées directes ── */}
          <div className="mt-14 border-t border-white/10 pt-8">
            <p className={labelClass}>
              {labels.orDirectly ?? (locale === "fr" ? "Ou directement" : "Or directly")}
            </p>
            <div className="mt-4 flex flex-wrap items-center gap-3">
              <a
                href="https://wa.me/33614830298"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2.5 rounded-full border border-white/20 px-5 py-2.5 text-[11px] tracking-[0.16em] uppercase text-white/80 font-body transition-colors duration-200 hover:border-white/50 hover:text-white"
              >
                <FaWhatsapp className="h-4 w-4" />
                WhatsApp
              </a>
              <a
                href="tel:+33614830298"
                className="inline-flex items-center rounded-full bg-white px-5 py-2.5 text-[11px] tracking-[0.16em] uppercase text-black font-body font-medium transition-colors duration-200 hover:bg-white/90"
              >
                +33 6 14 83 02 98
              </a>
            </div>
            <address className="mt-5 text-sm not-italic leading-relaxed text-white/45 font-body">
              Boutique Immeuble Concorde,
              <br />
              en face de l&apos;échangeur — Carré 29-30
              <br />
              Tokpa Hoho, Cotonou
            </address>
          </div>
        </div>
      </div>

      {/* ── RIGHT — Carousel with quote ───────────────────────────── */}
      <div className="hidden lg:block relative flex-1 min-h-screen overflow-hidden">

        {/* Carousel images with cross-fade */}
        <AnimatePresence>
          <motion.div
            key={currentSlide}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 2, ease: "easeInOut" }}
            className="absolute inset-0"
          >
            <Image
              src={SLIDES[currentSlide]}
              alt="Welconcept — contact"
              fill
              className="object-cover object-center"
              sizes="50vw"
              priority={currentSlide === 0}
            />
          </motion.div>
        </AnimatePresence>

        {/* Dark gradient overlay — toujours au-dessus */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/10 to-black/60 z-10" />

        {/* Bottom quote — fixe */}
        <div className="absolute bottom-14 left-10 right-10 z-20">
          <div className="w-8 h-px bg-white/40 mb-5" />
          <blockquote className="font-heading text-4xl text-white font-light italic leading-snug">
            {labels.quoteText}
          </blockquote>
        </div>
      </div>
    </section>
  );
}
